import { Capacitor } from "@capacitor/core";
import { logger } from "@/lib/logger";

// Non-sensitive user preference flag (boolean only). Actual biometric
// credentials never touch JS — they live in the OS keychain via the Capacitor
// plugin. We namespace the key to avoid collisions with other localStorage
// entries and make the intent explicit.
const LS_KEY = "patrimonio.pref.biometric_enabled";

/** True if the app runs inside a native Capacitor shell (iOS/Android). */
export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform?.() ?? false;
  } catch {
    return false;
  }
}

/**
 * Loads the biometric plugin only on native (avoids web-warning noise).
 * Returns null in browsers.
 */
async function getPlugin() {
  if (!isNativeApp()) return null;
  try {
    const mod = await import("@aparajita/capacitor-biometric-auth");
    return mod.BiometricAuth;
  } catch (e) {
    logger.warn("biometric plugin", e);
    return null;
  }
}

/** Whether the current device supports biometric auth (only meaningful on native). */
export async function biometricSupported() {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const info = await plugin.checkBiometry();
    return !!info?.isAvailable;
  } catch (e) {
    logger.warn("biometric checkBiometry", e);
    return false;
  }
}

export function biometricPreference() {
  try {
    return localStorage.getItem(LS_KEY) === "1";
  } catch (e) {
    logger.warn("biometric preference read failed", e);
    return false;
  }
}

export function setBiometricPreference(on) {
  try {
    if (on) localStorage.setItem(LS_KEY, "1");
    else localStorage.removeItem(LS_KEY);
  } catch (e) {
    // localStorage may be unavailable (private mode, SSR). Non-fatal.
    logger.warn("biometric preference write failed", e);
  }
}

/**
 * Prompts biometric authentication. Resolves true if user is verified,
 * throws otherwise. Always returns true on web (no-op).
 */
export async function biometricAuthenticate({
  reason = "Desbloquea Patrimonio",
  title = "Patrimonio",
  subtitle = "Autentícate para acceder a tus finanzas",
  cancelTitle = "Cancelar",
} = {}) {
  if (!isNativeApp()) return true;
  const plugin = await getPlugin();
  if (!plugin) return true;
  try {
    await plugin.authenticate({
      reason,
      androidTitle: title,
      androidSubtitle: subtitle,
      cancelTitle,
      allowDeviceCredential: true,
    });
    return true;
  } catch (e) {
    logger.warn("biometric auth failed", e);
    throw e;
  }
}
